'use strict';

/**
 * Variable-name derivation for the answers adapter.
 *
 * Works out which variables a question object declares, from the question
 * object alone. Pure: nothing here reads the store, the DOM, or any global, so
 * the same question always yields the same names. Reading the values is
 * `./index.js`; reporting names the store cannot hold is `./undeclared.js`.
 */

/**
 * Record one variable name, in first-seen order, ignoring repeats.
 *
 * Repeats are the norm, not an edge case: a radio grid repeats one row's
 * variable across every cell of that row, and `phases` is a paginated view over
 * rows that `category_options` already listed.
 *
 * The membership index is prototype-free, so a variable named `constructor` or
 * `toString` cannot read as already seen.
 *
 * @param {string[]} names Accumulator, mutated in place.
 * @param {Object} seen Membership index, mutated in place.
 * @param {*} name Candidate variable name; ignored unless a non-empty string.
 */
function addName(names, seen, name) {
  if (typeof name !== 'string' || name === '' || seen[name] === true) {
    return;
  }
  seen[name] = true;
  names.push(name);
}

/**
 * Append the object entries of `source` to `items`.
 *
 * @param {Object[]} items Accumulator, mutated in place.
 * @param {*} source Candidate array; ignored unless an array.
 */
function appendItems(items, source) {
  if (!Array.isArray(source)) {
    return;
  }
  for (var i = 0; i < source.length; i++) {
    if (source[i] && typeof source[i] === 'object') {
      items.push(source[i]);
    }
  }
}

/**
 * Flatten every container of variable-declaring items a question may carry.
 * Three containers hold them, and a question may carry more than one:
 *
 *   - `response_options` — the choices (SingleMultiple) or columns (Grid).
 *   - `category_options` — the grid's rows, i.e. its subquestions.
 *   - `phases`           — a `displaymax`-sized chunking of one of the above,
 *                          for paged questions.
 *
 * `phases` overlaps whichever container it chunks, and the two can disagree
 * about a row: they are built separately, and a custom row order rewrites one
 * of them. Both are read and the union is kept, so a variable declared in
 * either is present — including rows on a page the respondent has not reached.
 * `addName` absorbs the overlap.
 *
 * @param {Object} question The question object.
 * @returns {Object[]} The items, in declaration order.
 */
function itemsOf(question) {
  var items = [];
  appendItems(items, question.response_options);
  appendItems(items, question.category_options);
  if (Array.isArray(question.phases)) {
    for (var i = 0; i < question.phases.length; i++) {
      appendItems(items, question.phases[i]);
    }
  }
  return items;
}

/**
 * Does any item declare a variable of its own?
 *
 * This decides whether `question.input_id` is an answer variable, because the
 * templates use it two different ways. Where items declare nothing —
 * a radio question — `input_id` is the shared `name` of every input, so it is
 * the question's one variable. Where items declare their own names — a checkbox
 * question's `input_name`, a grid row's `response_inputs` — `input_id` is only a
 * DOM id prefix (`id="r-{{input_id}}-{{code}}"`, `id="{{input_id}}-grid"`) and
 * is never submitted.
 *
 * `input_type` is read first because it is the field the template itself
 * branches on: `_response-item.hbs` renders `name="{{input_id}}"` under
 * `{{#ifequal input_type 'radio'}}` and `name="{{input_name}}"` under
 * `{{#ifequal input_type 'checkbox'}}`. Only `checkbox` is checked, not
 * `radio`, because it is the only value that moves the variable onto the
 * items; `dropdown` questions carry `input_type: 'radio'` and submit
 * `input_id` from their `<select>`, and a grid carries the input type of its
 * cells while still keeping `input_id` as the table's DOM id.
 *
 * Trailers deliberately do not count. A radio question with an open trailer
 * still submits `input_id` for the radio itself.
 *
 * @param {Object} question The question object.
 * @param {Object[]} items Items from `itemsOf`.
 * @returns {boolean} `true` when at least one item names its own variable.
 */
function hasPerItemVariables(question, items) {
  if (question.input_type === 'checkbox') {
    return true;
  }
  for (var i = 0; i < items.length; i++) {
    if (
      typeof items[i].input_name === 'string' ||
      Array.isArray(items[i].response_inputs) ||
      typeof items[i].name === 'string' ||
      typeof items[i].input_id === 'string'
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Every variable name a question declares, in declaration order.
 * Covers the shapes SingleMultiple-family (single, multiple, dropdown) and
 * Grid-family questions (grid, grid-check, grid-open) use:
 *
 *   - `question.input_id` — the whole question's variable, for a question whose
 *     items declare none of their own. See `hasPerItemVariables`.
 *   - `item.input_name` — one variable per checkbox choice.
 *   - `item.response_inputs` — one variable per grid cell. A radio grid repeats
 *     the row's variable across the row; a check or open grid names each cell.
 *   - `item.trailer.input_id` — the text or popup input trailing a choice. Both
 *     the `open_trailer` and `popup` partials render `name="{{input_id}}"` from
 *     the trailer, so one rule covers both. `trailer.input_idx` is not included:
 *     it only ever appears in a DOM id or a `data-target`.
 *   - `item.name` and `item.input_id` — a row that names itself no other way.
 *     Scale renders its per-row hidden input as `name="{{name}}"`
 *     (`scale.hbs:50`) and Placement as `name="{{input_id}}"`
 *     (`placement.hbs:149`), both inside `{{#each category_options}}`. Read
 *     only where the row carries neither `response_inputs` nor `input_name`,
 *     because for a grid row `name` is the xor group rather than a control.
 *   - `question.dk_id` — the question-level "don't know" checkbox.
 *   - `question.passthru` keys — server-set values submitted verbatim from
 *     hidden inputs, such as `w1-cat_count` and `w1-ritem_count`.
 *
 * Widget families with bespoke variable names still declare variables this does
 * not derive, and the shapes differ in kind:
 *
 *   - Thermometer names two variables per row from one field, `resp_key` and
 *     `{{resp_key}}-mobile` (`thermometer.hbs:55,26`). No question field spells
 *     the suffixed one, so it needs its own rule rather than another lookup.
 *   - Rule submits `response_id` (`rule.hbs:26`), which nothing here reads. It
 *     also gates its dk markup on `dont_know` (`rule.hbs:34`) while still
 *     carrying `dk_id`, so a Rule question can report a `dk_id` that was never
 *     rendered — harmless per the inclusiveness note below, but not exact.
 *   - Address, Registration, Video and Upload were not traced here.
 *
 * Extend this function when a rule for one of them moves into the domain layer,
 * and cover the new shape with a fixture in `tests/variables.spec.js`. Until
 * then, treat a snapshot for one of those families as wrong rather than merely
 * empty.
 *
 * The list is inclusive rather than exact: `dk_id` and `passthru` are read
 * without consulting the flags that gate their markup, because widget families
 * gate them on different fields (`dk`, `dont_know`). Naming a variable that was
 * never rendered is safe for the reason given under `answersForWidget`: nothing
 * ever writes it, so it reads as `undefined`, which is the same thing a rendered
 * but untouched control reads as. It is safe, not free — see the warning there
 * about what the key set does and does not tell a rule.
 *
 * @param {Object} question The question object.
 * @returns {string[]} Variable names, deduplicated.
 */
export function variablesOf(question) {
  var names = answerVariablesOf(question);
  var seen = Object.create(null);
  var i;
  for (i = 0; i < names.length; i++) {
    seen[names[i]] = true;
  }
  if (question.passthru && typeof question.passthru === 'object') {
    for (var key in question.passthru) {
      if (question.passthru.hasOwnProperty(key)) {
        addName(names, seen, key);
      }
    }
  }
  return names;
}

/**
 * The variables of a question a respondent can actually answer.
 * `variablesOf` minus `question.passthru`. That is the whole difference, and it
 * is the one a rule cares about: `passthru` names server-computed values —
 * `w1-cat_count` and the like — that the widget renders into hidden inputs and
 * nobody ever edits. They are in the store because `utils/lib/answer-seed.js`
 * merges `question.passthru` straight off the API response at page load.
 *
 * So a `passthru` key holds a value on a page the respondent has not touched,
 * while every variable they could answer is still unset. Counting answered
 * variables across a whole `answersForWidget` snapshot therefore reports
 * "something is answered" for an untouched question. Count over this list
 * instead.
 *
 * `dk_id` IS included: choosing "don't know" is an answer, so a rule asking
 * whether the question was answered must see it.
 *
 * The values are still read with `answersForWidget`; this only says which keys
 * of that snapshot carry respondent input. A rule that needs a `passthru` value
 * should read `question.passthru` directly — the question object already
 * carries it, so nothing is lost by keeping it out of the count.
 *
 * @param {Object} question The question object.
 * @returns {string[]} Variable names, deduplicated, in declaration order.
 */
export function answerVariablesOf(question) {
  var names = [];
  var seen = Object.create(null);
  var items;
  var i;
  if (!question || typeof question !== 'object') {
    return names;
  }
  items = itemsOf(question);
  if (!hasPerItemVariables(question, items)) {
    addName(names, seen, question.input_id);
  }
  for (i = 0; i < items.length; i++) {
    var item = items[i];
    if (Array.isArray(item.response_inputs)) {
      for (var j = 0; j < item.response_inputs.length; j++) {
        addName(names, seen, item.response_inputs[j]);
      }
    }
    addName(names, seen, item.input_name);
    // Only where the item names itself no other way. A grid row carries both
    // `response_inputs` and a `name`, but there `name` is the xor group
    // (`data-xor-group="{{category_name}}"`) and a `<div id>`, never a control,
    // so reading it would add a variable the store never holds.
    if (
      !Array.isArray(item.response_inputs) &&
      typeof item.input_name !== 'string'
    ) {
      addName(names, seen, item.name);
      addName(names, seen, item.input_id);
    }
    if (item.trailer) {
      addName(names, seen, item.trailer.input_id);
    }
  }
  addName(names, seen, question.dk_id);
  return names;
}
